'use client'

import * as React from 'react'
import type { SkillLevel } from '@web/gen/service/creativefabrica/social/v2/types_pb'

export interface UploadPreview {
  id?: string
  url: string
  type: 'image' | 'video'
  uploading?: boolean
  source?: 'upload' | 'library'
  tempId?: string
}

export interface Asset {
  id: string
  name: string
  thumbnailUrl?: string
  category?: string | null
}

export interface Material {
  id: string
  name: string
}

export interface Machine {
  id: string
  name: string
}

export interface Theme {
  id: string
  name: string
}

export interface ProjectStep {
  id: string
  description: string
  media: UploadPreview[]
  uploadIds?: string[]
  videoIds?: string[]
  isValid?: boolean
}

export interface ProjectDraftContextValue {
  description: string
  setDescription: React.Dispatch<React.SetStateAction<string>>

  uploads: UploadPreview[]
  setUploads: React.Dispatch<React.SetStateAction<UploadPreview[]>>

  assets: Asset[]
  setAssets: React.Dispatch<React.SetStateAction<Asset[]>>

  materials: Material[]
  setMaterials: React.Dispatch<React.SetStateAction<Material[]>>

  machines: Machine[]
  setMachines: React.Dispatch<React.SetStateAction<Machine[]>>

  theme: Theme | null
  setTheme: React.Dispatch<React.SetStateAction<Theme | null>>

  skillLevel: SkillLevel | null
  setSkillLevel: React.Dispatch<React.SetStateAction<SkillLevel | null>>

  steps: ProjectStep[]
  setSteps: React.Dispatch<React.SetStateAction<ProjectStep[]>>

  resetDraft: () => void
  activeInstructionMedia: { stepIndex: number | null } | null
  setActiveInstructionMedia: React.Dispatch<
    React.SetStateAction<{ stepIndex: number | null } | null>
  >
  validateStep: (step: ProjectStep) => boolean
  hasValidated: boolean
  setHasValidated: React.Dispatch<React.SetStateAction<boolean>>
}

const ProjectDraftContext = React.createContext<ProjectDraftContextValue | undefined>(undefined)

export const ProjectDraftProvider = ({ children }: React.PropsWithChildren) => {
  const [description, setDescription] = React.useState('')
  const [uploads, setUploads] = React.useState<UploadPreview[]>([])
  const [assets, setAssets] = React.useState<Asset[]>([])
  const [materials, setMaterials] = React.useState<Material[]>([])
  const [machines, setMachines] = React.useState<Machine[]>([])
  const [theme, setTheme] = React.useState<Theme | null>(null)
  const [skillLevel, setSkillLevel] = React.useState<SkillLevel | null>(null)
  const [steps, setSteps] = React.useState<ProjectStep[]>([])
  const [activeInstructionMedia, setActiveInstructionMedia] = React.useState<{
    stepIndex: number | null
  } | null>(null)
  const [hasValidated, setHasValidated] = React.useState(false)

  const resetDraft = React.useCallback(() => {
    setDescription('')
    setUploads([])
    setAssets([])
    setMaterials([])
    setMachines([])
    setTheme(null)
    setSkillLevel(null)
    setSteps([])
  }, [])

  const validateStep = React.useCallback((step: ProjectStep) => {
    setHasValidated(true)
    return step.description.trim().length > 0 || step.media.length > 0
  }, [])

  const value: ProjectDraftContextValue = {
    description,
    setDescription,
    uploads,
    setUploads,
    assets,
    setAssets,
    materials,
    setMaterials,
    machines,
    setMachines,
    theme,
    setTheme,
    skillLevel,
    setSkillLevel,
    steps,
    setSteps,
    resetDraft,
    activeInstructionMedia,
    setActiveInstructionMedia,
    validateStep,
    hasValidated,
    setHasValidated,
  }

  return <ProjectDraftContext.Provider value={value}>{children}</ProjectDraftContext.Provider>
}

export const useProjectDraft = () => {
  const ctx = React.useContext(ProjectDraftContext)
  if (!ctx) {
    throw new Error('useProjectDraft must be used within a ProjectDraftProvider')
  }
  return ctx
}
